import React, { useEffect, useState } from "react";
import axios from "axios";
import DataTable from "react-data-table-component";
import { toast } from "react-hot-toast";
import {
    Backdrop,
    Box,
    Button,
    Chip,
    CircularProgress,
    Fade,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Modal,
    Paper,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import {
    FiCalendar,
    FiClock,
    FiGlobe,
    FiPlus,
    FiRefreshCw,
    FiTrash2,
    FiX,
} from "react-icons/fi";

const AppDowntime = () => {
    const [downtimes, setDowntimes] = useState([]);
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [isGlobal, setIsGlobal] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [filterTenantId, setFilterTenantId] = useState("");

    // Form fields
    const [formTenantId, setFormTenantId] = useState("");
    const [formStartTime, setFormStartTime] = useState("");
    const [formEndTime, setFormEndTime] = useState("");

    const BASE_URL = process.env.REACT_APP_BASE_URL;
    const getToken = () => sessionStorage.getItem("access_token");

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        toast.remove();
        const toastId = toast.loading("Fetching downtime data...");

        try {
            const [downtimeRes, tenantRes] = await Promise.all([
                axios({
                    method: "get",
                    url: `${BASE_URL}/admin/app-downtime`,
                    headers: { Authorization: "Bearer " + getToken() },
                }),
                axios({
                    method: "get",
                    url: `${BASE_URL}/admin/company`,
                    headers: { Authorization: "Bearer " + getToken() },
                }),
            ]);

            if (downtimeRes.data.success) {
                setDowntimes(downtimeRes.data.body || []);
            }
            if (tenantRes.data.success) {
                setTenants(tenantRes.data.body || []);
            }

            toast.success("Data fetched successfully", { id: toastId });
        } catch (error) {
            console.error(error);
            toast.error(
                error.response?.data?.message || error.message || "Failed to fetch data",
                { id: toastId }
            );
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (global = false) => {
        setIsGlobal(global);
        setFormTenantId("");
        setFormStartTime("");
        setFormEndTime("");
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
    };

    const handleSubmit = async () => {
        if (!formStartTime || !formEndTime) {
            toast.error("Start time and end time are required");
            return;
        }
        if (new Date(formEndTime) <= new Date(formStartTime)) {
            toast.error("End time must be after start time");
            return;
        }
        if (!isGlobal && !formTenantId) {
            toast.error("Please select a tenant");
            return;
        }

        setSubmitting(true);
        const toastId = toast.loading(
            isGlobal ? "Creating global downtime..." : "Creating downtime..."
        );

        try {
            const url = isGlobal
                ? `${BASE_URL}/admin/app-downtime/global`
                : `${BASE_URL}/admin/app-downtime`;

            const body = isGlobal
                ? { start_time: formStartTime, end_time: formEndTime }
                : { tenant_id: Number(formTenantId), start_time: formStartTime, end_time: formEndTime };

            const response = await axios({
                method: "post",
                url,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: "Bearer " + getToken(),
                },
                data: body,
            });

            if (response.data.success) {
                toast.success(
                    isGlobal
                        ? `Global downtime created for ${response.data.body?.inserted || "all"} tenants`
                        : "Downtime created successfully",
                    { id: toastId }
                );
                handleCloseModal();
                fetchData();
            } else {
                toast.error(response.data.message || "Failed to create", { id: toastId });
            }
        } catch (error) {
            console.error(error);
            toast.error(
                error.response?.data?.message || error.message || "Failed to create",
                { id: toastId }
            );
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        setDeletingId(id);
        const toastId = toast.loading("Deleting downtime...");

        try {
            const response = await axios({
                method: "delete",
                url: `${BASE_URL}/admin/app-downtime/${id}`,
                headers: { Authorization: "Bearer " + getToken() },
            });

            if (response.data.success) {
                toast.success("Downtime deleted", { id: toastId });
                setDowntimes((prev) => prev.filter((d) => d.id !== id));
            } else {
                toast.error(response.data.message || "Failed to delete", { id: toastId });
            }
        } catch (error) {
            console.error(error);
            toast.error(
                error.response?.data?.message || error.message || "Failed to delete",
                { id: toastId }
            );
        } finally {
            setDeletingId(null);
        }
    };

    const getStatusChip = (row) => {
        const now = new Date();
        const start = new Date(row.start_time);
        const end = new Date(row.end_time);

        if (now >= start && now <= end) {
            return (
                <Chip
                    size="small"
                    label="Active Now"
                    sx={{
                        fontWeight: 800,
                        bgcolor: "rgba(239, 68, 68, 0.12)",
                        color: "rgb(220, 38, 38)",
                    }}
                />
            );
        } else if (now < start) {
            return (
                <Chip
                    size="small"
                    label="Upcoming"
                    sx={{
                        fontWeight: 800,
                        bgcolor: "rgba(234, 179, 8, 0.12)",
                        color: "rgb(161, 98, 7)",
                    }}
                />
            );
        } else {
            return (
                <Chip
                    size="small"
                    label="Expired"
                    sx={{
                        fontWeight: 800,
                        bgcolor: "rgba(15, 23, 42, 0.08)",
                        color: "rgba(15, 23, 42, 0.5)",
                    }}
                />
            );
        }
    };

    const filteredDowntimes = filterTenantId
        ? downtimes.filter((d) => d.tenant_id === Number(filterTenantId))
        : downtimes;

    const columns = [
        {
            name: "S.No",
            selector: (row, idx) => idx + 1,
            sortable: true,
            width: "80px",
        },
        {
            name: "Tenant ID",
            selector: (row) => row.tenant_id,
            sortable: true,
            width: "110px",
        },
        {
            name: "Tenant Name",
            selector: (row) => row.tenant_name || "-",
            sortable: true,
            minWidth: "160px",
        },
        {
            name: "Start Time",
            selector: (row) => row.start_time?.replace('T', ' ').replace('Z', ''),
            sortable: true,
            minWidth: "200px",
        },
        {
            name: "End Time",
            selector: (row) => row.end_time?.replace('T', ' ').replace('Z', ''),
            sortable: true,
            minWidth: "200px",
        },
        {
            name: "Status",
            cell: (row) => getStatusChip(row),
            width: "130px",
        },
        {
            name: "Created At",
            selector: (row) => new Date(row.created_at).toLocaleString(),
            sortable: true,
            minWidth: "200px",
        },
        {
            name: "Action",
            cell: (row) => (
                <Tooltip title="Delete downtime">
                    <IconButton
                        onClick={() => handleDelete(row.id)}
                        disabled={deletingId === row.id}
                        size="small"
                        sx={{
                            border: "1px solid",
                            borderColor: "rgba(239, 68, 68, 0.4)",
                            color: "rgb(239, 68, 68)",
                            transition: "transform 160ms ease, background-color 160ms ease",
                            "&:hover": {
                                transform: "translateY(-1px)",
                                backgroundColor: "rgba(239, 68, 68, 0.08)",
                            },
                        }}
                    >
                        {deletingId === row.id ? (
                            <CircularProgress size={14} color="inherit" />
                        ) : (
                            <FiTrash2 />
                        )}
                    </IconButton>
                </Tooltip>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: "100px",
        },
    ];

    const customStyles = {
        headCells: {
            style: {
                fontWeight: 700,
                fontSize: "12px",
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                color: "rgba(15, 23, 42, 0.7)",
            },
        },
        rows: {
            style: {
                minHeight: "62px",
            },
        },
    };

    return (
        <div className="page-content">
            <Paper
                elevation={0}
                sx={{
                    p: { xs: 2, md: 3 },
                    borderRadius: 3,
                    border: "1px solid rgba(15, 23, 42, 0.08)",
                    background:
                        "radial-gradient(1200px circle at 0% 0%, rgba(251, 146, 60, 0.14), transparent 45%), radial-gradient(900px circle at 100% 0%, rgba(239, 68, 68, 0.12), transparent 45%), #fff",
                }}
            >
                <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={2}
                    alignItems={{ xs: "flex-start", md: "center" }}
                    justifyContent="space-between"
                    sx={{ mb: 2 }}
                >
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
                            App Downtime Management
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ color: "rgba(15, 23, 42, 0.65)", mt: 0.5 }}
                        >
                            Schedule downtime windows per tenant or globally for all tenants.
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Chip
                            label={loading ? "Loading" : `${filteredDowntimes.length} entries`}
                            size="small"
                            sx={{
                                fontWeight: 700,
                                bgcolor: "rgba(239, 68, 68, 0.08)",
                                color: "rgb(239, 68, 68)",
                            }}
                        />
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                            <InputLabel>Filter by Tenant</InputLabel>
                            <Select
                                value={filterTenantId}
                                onChange={(e) => setFilterTenantId(e.target.value)}
                                label="Filter by Tenant"
                            >
                                <MenuItem value="">All Tenants</MenuItem>
                                {tenants.map((t) => (
                                    <MenuItem key={t.tenant_id} value={t.tenant_id}>
                                        {t.name} ({t.tenant_id})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <Button
                            variant="outlined"
                            onClick={fetchData}
                            startIcon={<FiRefreshCw />}
                            sx={{
                                textTransform: "none",
                                borderRadius: 2,
                                transition: "transform 160ms ease",
                                "&:hover": { transform: "translateY(-1px)" },
                            }}
                        >
                            Refresh
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => handleOpenModal(false)}
                            startIcon={<FiPlus />}
                            sx={{
                                textTransform: "none",
                                borderRadius: 2,
                                boxShadow: "none",
                                transition: "transform 160ms ease, box-shadow 160ms ease",
                                "&:hover": {
                                    transform: "translateY(-1px)",
                                    boxShadow: "0 10px 25px rgba(2, 6, 23, 0.12)",
                                },
                            }}
                        >
                            Add Downtime
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => handleOpenModal(true)}
                            startIcon={<FiGlobe />}
                            color="error"
                            sx={{
                                textTransform: "none",
                                borderRadius: 2,
                                boxShadow: "none",
                                transition: "transform 160ms ease, box-shadow 160ms ease",
                                "&:hover": {
                                    transform: "translateY(-1px)",
                                    boxShadow: "0 10px 25px rgba(239, 68, 68, 0.25)",
                                },
                            }}
                        >
                            Global Downtime
                        </Button>
                    </Stack>
                </Stack>

                <Box
                    sx={{
                        borderRadius: 3,
                        overflow: "hidden",
                        border: "1px solid rgba(15, 23, 42, 0.08)",
                        backgroundColor: "rgba(255,255,255,0.75)",
                        backdropFilter: "blur(10px)",
                    }}
                >
                    <DataTable
                        title=""
                        data={filteredDowntimes}
                        columns={columns}
                        striped
                        highlightOnHover
                        pointerOnHover
                        pagination
                        progressPending={loading}
                        customStyles={customStyles}
                    />
                </Box>
            </Paper>

            {/* Create Downtime Modal */}
            <Modal
                open={showModal}
                onClose={handleCloseModal}
                closeAfterTransition
                BackdropComponent={Backdrop}
                BackdropProps={{ timeout: 500 }}
            >
                <Fade in={showModal}>
                    <Box
                        sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            width: "90%",
                            maxWidth: "520px",
                            bgcolor: "rgba(255, 255, 255, 0.95)",
                            backdropFilter: "blur(12px)",
                            boxShadow: "0 30px 80px rgba(2, 6, 23, 0.35)",
                            borderRadius: 3,
                            p: 4,
                        }}
                    >
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ mb: 3 }}
                        >
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                    {isGlobal ? "Schedule Global Downtime" : "Schedule Downtime"}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ color: "rgba(15, 23, 42, 0.65)", mt: 0.25 }}
                                >
                                    {isGlobal
                                        ? "This will create downtime entries for ALL tenants."
                                        : "Select a tenant and set the downtime window."}
                                </Typography>
                            </Box>
                            <Tooltip title="Close">
                                <IconButton
                                    onClick={handleCloseModal}
                                    sx={{
                                        border: "1px solid rgba(15, 23, 42, 0.12)",
                                        transition:
                                            "transform 160ms ease, background-color 160ms ease",
                                        "&:hover": {
                                            transform: "rotate(90deg)",
                                            backgroundColor: "rgba(15, 23, 42, 0.06)",
                                        },
                                    }}
                                >
                                    <FiX />
                                </IconButton>
                            </Tooltip>
                        </Stack>

                        {isGlobal && (
                            <Box
                                sx={{
                                    mb: 3,
                                    p: 2,
                                    borderRadius: 2,
                                    bgcolor: "rgba(239, 68, 68, 0.06)",
                                    border: "1px solid rgba(239, 68, 68, 0.15)",
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{ color: "rgb(185, 28, 28)", fontWeight: 600 }}
                                >
                                    ⚠️ This will create downtime for all {tenants.length} tenants at
                                    once.
                                </Typography>
                            </Box>
                        )}

                        <Stack spacing={3}>
                            {!isGlobal && (
                                <FormControl fullWidth>
                                    <InputLabel>Select Tenant</InputLabel>
                                    <Select
                                        value={formTenantId}
                                        onChange={(e) => setFormTenantId(e.target.value)}
                                        label="Select Tenant"
                                    >
                                        {tenants.map((t) => (
                                            <MenuItem key={t.tenant_id} value={t.tenant_id}>
                                                {t.name} ({t.tenant_id})
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}

                            <TextField
                                label="Start Time"
                                type="datetime-local"
                                value={formStartTime}
                                onChange={(e) => setFormStartTime(e.target.value)}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                            />

                            <TextField
                                label="End Time"
                                type="datetime-local"
                                value={formEndTime}
                                onChange={(e) => setFormEndTime(e.target.value)}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                            />

                            <Button
                                variant="contained"
                                onClick={handleSubmit}
                                disabled={submitting}
                                color={isGlobal ? "error" : "primary"}
                                startIcon={
                                    submitting ? (
                                        <CircularProgress size={16} color="inherit" />
                                    ) : isGlobal ? (
                                        <FiGlobe />
                                    ) : (
                                        <FiCalendar />
                                    )
                                }
                                fullWidth
                                sx={{
                                    textTransform: "none",
                                    borderRadius: 2,
                                    py: 1.5,
                                    fontWeight: 700,
                                    boxShadow: "none",
                                    transition:
                                        "transform 160ms ease, box-shadow 160ms ease",
                                    "&:hover": {
                                        transform: "translateY(-1px)",
                                        boxShadow: "0 10px 25px rgba(2, 6, 23, 0.15)",
                                    },
                                }}
                            >
                                {submitting
                                    ? "Creating..."
                                    : isGlobal
                                    ? "Create Global Downtime"
                                    : "Create Downtime"}
                            </Button>
                        </Stack>
                    </Box>
                </Fade>
            </Modal>
        </div>
    );
};

export default AppDowntime;
